import { convertOrientationToRadians, convertAngleToRadians } from '../angle-utils.js';
import {
  colorStopToString,
  calculateLinearStop,
  fixTransparentColorStops,
} from '../color-utils.js';

/**
 * Calculate CSS gradient line length
 * Per CSS Images Module Level 3, the gradient line length is:
 * abs(W * sin(θ)) + abs(H * cos(θ))
 * where θ is the gradient angle measured from vertical (0deg = to top)
 * @param {number} width - Rectangle width
 * @param {number} height - Rectangle height
 * @param {number} angleRad - Gradient angle in radians (CSS convention: 0 = to top)
 * @returns {number} Gradient line length
 */
function calculateGradientLineLength(width, height, angleRad) {
  return Math.abs(width * Math.sin(angleRad)) + Math.abs(height * Math.cos(angleRad));
}

/**
 * Calculate gradient line start and end points
 * @param {number} width - Rectangle width
 * @param {number} height - Rectangle height
 * @param {number} angleRad - Gradient angle in radians (CSS convention: 0 = to top)
 * @returns {Object} { start: {x, y}, end: {x, y} }
 */
function calculateGradientLinePoints(width, height, angleRad) {
  const cx = width / 2;
  const cy = height / 2;
  const lineLength = calculateGradientLineLength(width, height, angleRad);
  const halfLength = lineLength / 2;

  // CSS gradient angle: 0deg = to top, 90deg = to right
  // Convert to standard math angle: 0 = right, counter-clockwise positive
  // CSS 0deg (to top) = math 90deg (π/2)
  // CSS 90deg (to right) = math 0deg (0)
  const mathAngle = Math.PI / 2 - angleRad;

  const dx = Math.cos(mathAngle) * halfLength;
  const dy = -Math.sin(mathAngle) * halfLength; // Negative because canvas Y is inverted

  return {
    start: { x: cx - dx, y: cy + dy },
    end: { x: cx + dx, y: cy - dy },
  };
}

/**
 * Render a linear gradient
 * @param {CanvasRenderingContext2D} context - Canvas context
 * @param {Object} gradientDef - Gradient definition from parser
 * @param {Object} bounds - Bounding box { x, y, w, h }
 * @returns {CanvasGradient} Canvas gradient object
 */
export function renderLinearGradient(context, gradientDef, { x, y, w, h }) {
  // Get gradient angle in radians (CSS convention: 0 = to top)
  let angleRad;
  if (gradientDef.orientation?.value) {
    angleRad = convertOrientationToRadians(gradientDef.orientation);
  } else if (gradientDef.orientation) {
    angleRad = convertAngleToRadians(gradientDef.orientation);
  } else {
    angleRad = Math.PI; // Default: to bottom (180deg)
  }

  // Calculate gradient line points using CSS spec formula
  const { start, end } = calculateGradientLinePoints(w, h, angleRad);

  const gradient = context.createLinearGradient(x + start.x, y + start.y, x + end.x, y + end.y);

  // Clone colorStops to avoid mutating the original
  const colorStops = gradientDef.colorStops.map((stop) => ({ ...stop }));

  // Fix transparent color stops to match CSS premultiplied alpha behavior
  fixTransparentColorStops(colorStops);

  const distance = calculateGradientLineLength(w, h, angleRad);

  let lastPosition = 0;
  let lastColorValue = null;

  colorStops.forEach((stop, index) => {
    const position = calculateLinearStop(stop, index, colorStops.length, distance);
    const colorValue = colorStopToString(stop);

    if (colorValue) {
      const clampedPosition = Math.max(0, Math.min(1, position));
      gradient.addColorStop(clampedPosition, colorValue);
      lastPosition = clampedPosition;
      lastColorValue = colorValue;
    }
  });

  // If the last color stop is not at 100%, extend it to 100%
  // This matches CSS behavior where the last color continues to the edge
  if (lastPosition < 1 && lastColorValue) {
    gradient.addColorStop(1, lastColorValue);
  }

  return gradient;
}
